import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { studentsStore } from 'pages/Students/model/studentsStore'

const scoreMatch = (name, grade, query) => {
	if (!query) return 0
	const q = query.toLowerCase()
	const n = (name || '').toLowerCase()

	if (!n.includes(q) && !(grade && String(grade).toLowerCase().includes(q))) {
		return 0
	}

	if (n.startsWith(q)) return 4
	if (n.split(' ').some(part => part.startsWith(q))) return 3
	if (grade && String(grade).toLowerCase().startsWith(q)) return 2
	return 1
}

const isStudentsPage = pathname => pathname === '/students' || pathname.startsWith('/students/')

export const useStudentSearch = () => {
	const location = useLocation()
	const onStudentsPage = isStudentsPage(location.pathname)
	// On Students page, sync with store so header search filters the list by name
	const [query, setQuery] = useState(onStudentsPage ? (studentsStore.searchQuery || '') : '')
	const [isOpen, setIsOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const containerRef = useRef(null)
	const navigate = useNavigate()

	// Keep header input in sync with store when on Students page (e.g. after navigation)
	useEffect(() => {
		if (onStudentsPage && studentsStore.searchQuery !== query) {
			setQuery(studentsStore.searchQuery || '')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onStudentsPage, studentsStore.searchQuery])

	useEffect(() => {
		let cancelled = false

		const load = async () => {
			const needsAvatarRefresh = (studentsStore.students || []).some(
				s => !s?.avatarKey,
			)
			if (studentsStore.students?.length > 0 && !needsAvatarRefresh) return

			setIsLoading(true)
			try {
				await ensureStudentsLoaded()
			} catch (e) {
			} finally {
				if (!cancelled) setIsLoading(false)
			}
		}

		load()

		return () => {
			cancelled = true
		}
	}, [])

	const results = useMemo(() => {
		const students = studentsStore.students || []
		const trimmed = query.trim()
		if (!trimmed) return []

		return students
			.map(st => ({
				student: st,
				score: scoreMatch(st.name, st.grade, trimmed),
			}))
			.filter(x => x.score > 0)
			.sort((a, b) => b.score - a.score || a.student.name.localeCompare(b.student.name))
			.slice(0, 8)
			.map(x => x.student)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query])

	const handleChange = e => {
		const value = e.target.value
		setQuery(value)
		setIsOpen(!!value)
		if (onStudentsPage) {
			studentsStore.setSearchQuery(value)
		}
	}

	const handleFocus = () => {
		if (query) setIsOpen(true)
	}

	const handleSelectStudent = student => {
		if (!student?.id) return
		setIsOpen(false)
		setQuery('')
		const normalizedId = String(student.id).trim()
		const encodedId = encodeURIComponent(normalizedId)
		navigate(`/student_profile/${encodedId}`)
	}

	useEffect(() => {
		const handleClickOutside = e => {
			if (!containerRef.current) return
			if (!containerRef.current.contains(e.target)) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	return {
		query,
		isOpen: isOpen && results.length > 0,
		isLoading,
		results,
		containerRef,
		handleChange,
		handleFocus,
		setIsOpen,
		handleSelectStudent,
	}
}
